import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Chip,
  Card,
  CardContent,
  Alert,
  Fade,
  alpha,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  AttachMoney,
  CalendarToday,
  Person,
  LocalHospital,
  LinkOff,
  Link,
  Sync,
  CheckCircle,
  CloudUpload,
} from '@mui/icons-material';
import { getPayments, getPaymentStats, matchPayments, uploadPaymentFilesBulk, PaymentRecord, PaymentStats, BulkUploadFileResult } from '../api/api';

const PaymentsList: React.FC = () => {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Per-file outcome of the last bulk upload. Kept separate from the banner so
  // a partial import shows exactly which spreadsheets failed and why.
  const [uploadResults, setUploadResults] = useState<BulkUploadFileResult[]>([]);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentsData, statsData] = await Promise.all([
        getPayments({ limit: 500 }),
        getPaymentStats()
      ]);
      setPayments(paymentsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      setError('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleMatchPayments = async () => {
    setMatching(true);
    try {
      const result = await matchPayments();
      setSuccess(`Successfully matched ${result.matched_count} payments!`);
      fetchData(); // Refresh data
    } catch (error) {
      setError('Failed to match payments');
    } finally {
      setMatching(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    if (selected.length === 0) return;

    const isExcel = (name: string) => name.endsWith('.xlsx') || name.endsWith('.xls');
    const rejected = selected.filter((f) => !isExcel(f.name));
    const accepted = selected.filter((f) => isExcel(f.name));

    if (accepted.length === 0) {
      setError('Please select Excel files (.xlsx or .xls)');
      event.target.value = '';
      return;
    }

    setUploading(true);
    setUploadResults([]);
    try {
      // One request for the whole selection: the server processes each file
      // independently, so a bad spreadsheet fails on its own rather than
      // discarding the rest of the batch.
      const result = await uploadPaymentFilesBulk(accepted);
      setUploadResults(result.results);

      const parts = [`Imported ${result.total_imported} payments from ${result.files_succeeded} of ${result.files_received} file(s)`];
      if (result.total_duplicates) parts.push(`${result.total_duplicates} already present`);
      if (result.files_failed > 0) parts.push(`${result.files_failed} failed`);
      if (rejected.length > 0) parts.push(`${rejected.length} skipped (not Excel)`);

      if (result.files_failed > 0 || rejected.length > 0) {
        setError(parts.join(' · '));
      } else {
        setSuccess(parts.join(' · '));
      }
      fetchData(); // Refresh data
    } catch (error) {
      setError('Failed to upload payment file');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={40} sx={{ color: '#6366f1' }} />
        <Typography variant="body2" color="text.secondary">
          Loading payment data...
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Payment Tracking
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            Manage and match payment records with medical appointments
          </Typography>
        </Box>

        {/* Stats Cards */}
        {stats && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
            <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <AttachMoney sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.total_payments}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Payments
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {formatCurrency(stats.total_amount)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CheckCircle sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.matched_payments}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Matched
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={(stats.matched_payments / stats.total_payments) * 100}
                      sx={{ 
                        mt: 1, 
                        backgroundColor: alpha('#ffffff', 0.3),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#ffffff'
                        }
                      }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <LinkOff sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.unmatched_payments}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Unmatched
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {((stats.unmatched_payments / stats.total_payments) * 100).toFixed(1)}% remaining
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}

        {/* Actions */}
        <Stack spacing={3} sx={{ mb: 4 }}>
          {/* File Upload */}
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Upload Payment File
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload Excel files containing repasse/recebimento payment data
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    multiple
                    onChange={handleFileUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                    id="payment-file-upload"
                  />
                  <label htmlFor="payment-file-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                      disabled={uploading}
                      sx={{
                        borderColor: '#667eea',
                        color: '#667eea',
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        minWidth: 200,
                        '&:hover': {
                          borderColor: '#764ba2',
                          backgroundColor: alpha('#667eea', 0.05)
                        }
                      }}
                    >
                      {uploading ? 'Uploading...' : 'Upload Excel Files'}
                    </Button>
                  </label>
                </Stack>
              </Stack>

              {/* Per-file breakdown: a batch can partially succeed, and which
                  spreadsheet failed matters more than the aggregate count. */}
              {uploadResults.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Upload results ({uploadResults.filter((r) => r.success).length}/{uploadResults.length} succeeded)
                  </Typography>
                  <Stack spacing={0.5}>
                    {uploadResults.map((r) => (
                      <Box
                        key={r.filename}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: 14,
                          color: r.success ? 'success.main' : 'error.main',
                        }}
                      >
                        <span>{r.success ? '✓' : '✕'}</span>
                        <Box component="span" sx={{ fontWeight: 500 }}>{r.filename}</Box>
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {r.success
                            ? `${r.imported_count} record(s)${r.duplicate_count ? `, ${r.duplicate_count} already present` : ''}${r.file_date ? ` · ${r.file_date}` : ''}`
                            : r.error}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Payment Matching */}
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Automatic Payment Matching
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Match payment records with medical appointments based on doctor, patient, and amount
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={matching ? <CircularProgress size={20} /> : <Sync />}
                  onClick={handleMatchPayments}
                  disabled={matching || payments.length === 0}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    minWidth: 200
                  }}
                >
                  {matching ? 'Matching...' : 'Match Payments'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Payments Table */}
        <Paper 
          sx={{ 
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#6366f1', 0.05) }}>
                  <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday sx={{ fontSize: 16, color: '#6366f1' }} />
                      Data Pagamento
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person sx={{ fontSize: 16, color: '#6366f1' }} />
                      Médico
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalHospital sx={{ fontSize: 16, color: '#6366f1' }} />
                      Hospital
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Seguro</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AttachMoney sx={{ fontSize: 16, color: '#6366f1' }} />
                      Valor
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment, index) => (
                  <TableRow 
                    key={payment.id} 
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha('#6366f1', 0.02),
                        transition: 'all 0.2s ease'
                      },
                      borderBottom: index === payments.length - 1 ? 'none' : '1px solid #e2e8f0'
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDate(payment.payment_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {payment.doctor_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {payment.hospital_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {payment.patient_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {payment.insurance_company || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        color="#10b981"
                      >
                        {formatCurrency(payment.amount_paid)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.medical_record_id ? (
                        <Tooltip title="Matched with medical record">
                          <Chip
                            icon={<Link />}
                            label="Matched"
                            size="small"
                            sx={{
                              backgroundColor: alpha('#10b981', 0.1),
                              color: '#10b981',
                              fontWeight: 600,
                              borderRadius: 2
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not yet matched">
                          <Chip
                            icon={<LinkOff />}
                            label="Unmatched"
                            size="small"
                            sx={{
                              backgroundColor: alpha('#f59e0b', 0.1),
                              color: '#f59e0b',
                              fontWeight: 600,
                              borderRadius: 2
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {payments.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <AttachMoney sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
              No payment records found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Import Excel payment files to see payment tracking data here.
            </Typography>
          </Box>
        )}

        {/* Success/Error Messages */}
        {success && (
          <Alert 
            severity="success" 
            onClose={() => setSuccess(null)}
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          >
            {error}
          </Alert>
        )}
      </Box>
    </Fade>
  );
};

export default PaymentsList;